import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { makePostRequest } from "../../services/httpHelper";
import ErrorHandler from "../../services/ErrorHandler";

const RequestDemo = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    church: "",
    message: "",
    captchaAnswer: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [captcha, setCaptcha] = useState({ question: "", answer: 0 });

  // Generate captcha on mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ question: `${a} + ${b} = ?`, answer: a + b });
  };

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    // Validation
    const requiredFields = Object.entries(form).every(
      ([key, val]) => key === "captchaAnswer" || val.trim() !== ""
    );
    if (!requiredFields) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }

    if (parseInt(form.captchaAnswer, 10) !== captcha.answer) {
      setError("Incorrect answer to the math challenge.");
      setLoading(false);
      return;
    }

    const { captchaAnswer, ...formData } = form;

    try {
      const res = await makePostRequest("demo-request", formData);
      if (res && res.errorType) {
        const errorMessage = ErrorHandler(res);
        setError(errorMessage.toString());
      } else {
        setSuccess(true);
        setForm({ name: "", email: "", church: "", message: "", captchaAnswer: "" });
        generateCaptcha(); // generate a new challenge
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper id="demo">
      <div className="container">
        <Title>Request a Live Demo</Title>
        <SubTitle>
          Experience CS Church Management Software in action. We'll walk you
          through the features that matter most to your church.
        </SubTitle>

        {error && <ErrorMsg>{error}</ErrorMsg>}
        {success && <SuccessMsg>Your request has been submitted successfully! We will get back to you as soon as possible</SuccessMsg>}

        <Form onSubmit={handleSubmit}>
          <input
            type="text"
            name="name"
            placeholder="Your Name"
            value={form.name}
            onChange={handleChange}
            required
          />
          <input
            type="email"
            name="email"
            placeholder="Your Email"
            value={form.email}
            onChange={handleChange}
            required
          />
          <input
            type="text"
            name="church"
            placeholder="Church Name"
            value={form.church}
            onChange={handleChange}
            required
          />
          <textarea
            name="message"
            placeholder="Any specific questions or needs?"
            rows="4"
            value={form.message}
            onChange={handleChange}
          ></textarea>
          <input
            name="captchaAnswer"
            placeholder={`Spam check: ${captcha.question}`}
            value={form.captchaAnswer}
            onChange={handleChange}
            required
          />
          <SubmitButton
            type="submit"
            disabled={loading || !form.captchaAnswer.trim()}
          >
            {loading ? "Submitting..." : "Submit Request"}
          </SubmitButton>
        </Form>
      </div>
    </Wrapper>
  );
};

export default RequestDemo;

const Wrapper = styled.section`
  padding: 80px 20px;
  background: #f9fafb;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 30px;
  font-weight: 800;
  margin-bottom: 10px;
`;

const SubTitle = styled.p`
  max-width: 600px;
  margin: auto;
  font-size: 16px;
  color: #555;
  margin-bottom: 40px;
`;

const Form = styled.form`
  max-width: 600px;
  margin: auto;
  display: grid;
  gap: 20px;

  input,
  textarea {
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    width: 100%;
  }

  textarea {
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  background-color: #f97316;
  color: white;
  padding: 15px;
  border: none;
  font-weight: 600;
  font-size: 16px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background-color: #ea580c;
  }

  &:disabled {
    background-color: #fcd34d;
    cursor: not-allowed;
    color: #444;
  }
`;

const ErrorMsg = styled.p`
  color: #dc2626;
  font-weight: 600;
  margin-bottom: 20px;
`;

const SuccessMsg = styled.p`
  color: #16a34a;
  font-weight: 600;
  margin-bottom: 20px;
`;

